/* eslint-disable no-unused-vars */
/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";
import dayjs from "dayjs";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonBadge from "components/ArgonBadge";

// Argon Dashboard 2 MUI example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DetailedStatisticsCard from "examples/Cards/StatisticsCards/DetailedStatisticsCard";
import SalesTable from "examples/Tables/SalesTable";
import CategoriesList from "examples/Lists/CategoriesList";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import CloseIcon from "@mui/icons-material/Close";
import {
  Card,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// Argon Dashboard 2 MUI base styles
import typography from "assets/theme/base/typography";

// Dashboard layout components
import Slider from "layouts/dashboard/components/Slider";
import StatCard from "layouts/dashboard/components/StatCard/StatCard";
import ClientAnalyticsCard from "layouts/dashboard/components/ClientAnalyticsCard";
import TutorAnalyticsCard from "layouts/dashboard/components/TutorAnalyticsCard";

// Data
import gradientLineChartData from "layouts/dashboard/data/gradientLineChartData";
import salesTableData from "layouts/dashboard/data/salesTableData";
import categoriesListData from "layouts/dashboard/data/categoriesListData";

// API
import api from "utils/api";
import {
  getFinancialComparison,
  getAllTutorPaymentCycles,
  getRevenueSnapshot,
} from "services/financeService";
import { getAllTransactions } from "services/transactionService";

import { Link } from "react-router-dom";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const formatCurrency = (value = 0) => {
  const numericValue = Number(value);
  return currencyFormatter.format(Number.isFinite(numericValue) ? numericValue : 0);
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getParentIdValue = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value?._id || value?.id || null;
};

const normalizeCrmStatus = (status) =>
  String(status || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getStudentIdValue = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value?._id || value?.id || null;
};

const PROSPECT_EXCLUDED_STATUSES = new Set([
  "otm - completed",
  "retation & renewal",
  "won",
  "junk/ closed",
  "junk/closed",
  "junk closed",
  "no tutor",
]);

const TUTOR_FOUND_STATUSES = new Set([
  "demo done",
  "demo fixed",
  "negotiation",
  "negotiation & review",
  "otm",
  "otm - paid",
  "otm partial paid",
  "otm partial- paid",
  "otm partial - paid",
]);

const getLeadStatusUnits = (lead) => {
  const students = Array.isArray(lead?.parentId?.students) ? lead.parentId.students : [];

  if (students.length > 1) {
    return students.map((student) => {
      const studentId = getStudentIdValue(student);
      const studentStatus = Array.isArray(lead?.studentLevelStatuses)
        ? lead.studentLevelStatuses.find(
            (status) => getStudentIdValue(status?.studentId) === studentId
          )
        : null;

      return {
        lead,
        parentStatus: lead?.parentStatus,
        status: studentStatus?.followUpStatus || lead?.followUpStatus,
        studentId,
      };
    });
  }

  return [
    {
      lead,
      parentStatus: lead?.parentStatus,
      status: lead?.followUpStatus,
      studentId: null,
    },
  ];
};

const getLeadStageStats = (allLeads) => {
  const leadStatusUnits = Array.isArray(allLeads) ? allLeads.flatMap(getLeadStatusUnits) : [];
  const prospectUnits = leadStatusUnits.filter(
    (unit) => !PROSPECT_EXCLUDED_STATUSES.has(normalizeCrmStatus(unit.status))
  );
  const tutorFoundUnits = leadStatusUnits.filter((unit) =>
    TUTOR_FOUND_STATUSES.has(normalizeCrmStatus(unit.status))
  );
  const demoFixedUnits = leadStatusUnits.filter(
    (unit) => normalizeCrmStatus(unit.status) === "demo fixed"
  );
  const convertedUnits = leadStatusUnits.filter(
    (unit) => normalizeCrmStatus(unit.status) === "won"
  );

  return {
    leadStatusUnits,
    prospectUnits,
    tutorFoundUnits,
    demoFixedUnits,
    convertedUnits,
    prospect: prospectUnits.length,
    tutorFound: tutorFoundUnits.length,
    demoFixed: demoFixedUnits.length,
    converted: convertedUnits.length,
  };
};

function Default() {
  const { size } = typography;

  const fetchAllCrmEntries = async (params = {}) => {
    const entries = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await api.get("/crm", {
        params: { page, limit: 100, ...params },
      });

      const payload = response?.data?.data || {};
      const pageEntries = Array.isArray(payload.crmEntries) ? payload.crmEntries : [];
      const pagination = payload.pagination || {};

      entries.push(...pageEntries);
      totalPages = Number(pagination.totalPages) || 1;
      page += 1;
    } while (page <= totalPages);

    return entries;
  };

  const fetchAllParents = async () => {
    const parents = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await api.get("/parent", {
        params: { page, limit: 500 },
      });

      const pageParents = Array.isArray(response?.data?.data) ? response.data.data : [];
      const pagination = response?.data?.pagination || {};

      parents.push(...pageParents);
      totalPages = Number(pagination.pages) || 1;
      page += 1;
    } while (page <= totalPages);

    return parents;
  };

  const fetchAllMeetings = async (params = {}) => {
    const meetings = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await api.get("/meeting", {
        params: { page, limit: 100, ...params },
      });

      const pageMeetings = Array.isArray(response?.data?.meetings) ? response.data.meetings : [];
      const pagination = response?.data?.pagination || {};

      meetings.push(...pageMeetings);
      totalPages = Number(pagination.totalPages) || 1;
      page += 1;
    } while (page <= totalPages);

    return meetings;
  };

  const fetchAllTutors = async (params = {}) => {
    const tutors = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await api.get("/tutor", {
        params: { page, limit: 100, ...params },
      });

      const payload = response?.data?.data || {};
      const pageTutors = Array.isArray(payload.tutors) ? payload.tutors : [];
      const pagination = payload.pagination || {};

      tutors.push(...pageTutors);
      totalPages = Number(pagination.totalPages) || 1;
      page += 1;
    } while (page <= totalPages);

    return tutors;
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;

    if (typeof value === "string") {
      const match = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (match) {
        const day = Number(match[1]);
        const month = Number(match[2]) - 1;
        const year = Number(match[3]);
        const fallback = new Date(year, month, day);
        return Number.isNaN(fallback.getTime()) ? null : fallback;
      }
    }

    return null;
  };

  const getTutorCreatedDate = (tutor) =>
    parseDateValue(tutor?.createdAt || tutor?.joiningDate || null);
  const getMeetingInviteDate = (meeting) =>
    parseDateValue(meeting?.createdAt || meeting?.scheduledDate || meeting?.updatedAt || null);

  const [counts, setCounts] = useState({
    tutors: 0,
    parents: 0,
    students: 0,
    subjects: 0,
    categories: 0,
  });

  const [supportStats, setSupportStats] = useState({
    total: 0,
    open: 0,
    closed: 0,
    terminated: 0,
    onHold: 0,
  });

  const [financialComparison, setFinancialComparison] = useState({
    expected: { expense: 0, revenue: 0, grossMargin: 0 },
    current: { expense: 0, revenue: 0, grossMargin: 0 },
  });

  const [expectedFinancialRows, setExpectedFinancialRows] = useState([]);
  const [expectedFinancialLoading, setExpectedFinancialLoading] = useState(false);
  const [expectedFinancialOpen, setExpectedFinancialOpen] = useState(false);

  const [currentCycleFinancials, setCurrentCycleFinancials] = useState({
    expense: 0,
    revenue: 0,
    grossMargin: 0,
  });

  const [tutorSalaryOwed, setTutorSalaryOwed] = useState(0);

  const [clientAnalytics, setClientAnalytics] = useState({
    recentClients: [],
    clientsCreated28Days: 0,
    clientsCreatedPrevious28Days: 0,
    enquiries28Days: 0,
    enquiriesPrevious28Days: 0,
  });

  const [tutorAnalytics, setTutorAnalytics] = useState({
    recentTutors: [],
    tutorsCreated28Days: 0,
    tutorsCreatedPrevious28Days: 0,
    interviewInvites28Days: 0,
    interviewInvitesPrevious28Days: 0,
  });

  const [paymentStats, setPaymentStats] = useState({
    pendingPayments: { count: 0, amount: 0 },
    expiredPackages: { count: 0, amount: 0 },
    expectedRecurringPayment: 0,
    expectedTutorSalary: 0,
  });

  const [leadStats, setLeadStats] = useState({
    prospect: 0,
    tutorFound: 0,
    demoFixed: 0,
    converted: 0,
    totalConverted: 0,
    conversionRate: 0,
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const getTotalPages = (responseData) => {
          const pages =
            responseData?.data?.pagination?.totalPages ||
            responseData?.pagination?.totalPages ||
            responseData?.totalPages ||
            1;

          return Number.isFinite(Number(pages)) ? Number(pages) : 1;
        };

        const extractListData = (responseData, key) => {
          if (Array.isArray(responseData?.data?.[key])) return responseData.data[key];
          if (Array.isArray(responseData?.[key])) return responseData[key];
          if (Array.isArray(responseData?.data)) return responseData.data;
          if (Array.isArray(responseData)) return responseData;
          return [];
        };

        // Fetch tutor counts by status
        let tutorCount = 0;
        try {
          const tutors = [];
          let currentPage = 1;
          let totalPages = 1;

          do {
            const tutorResponse = await api.get("/tutor", {
              params: { page: currentPage, limit: 100 },
            });

            console.log("Tutor response:", tutorResponse.data);

            const pageTutors = extractListData(tutorResponse.data, "tutors");
            tutors.push(...pageTutors);

            totalPages = getTotalPages(tutorResponse.data);
            currentPage += 1;
          } while (currentPage <= totalPages);

          tutorCount = tutors.filter((tutor) => {
            const approvalStatus = String(tutor?.approvalStatus || "").toUpperCase();
            return !tutor?.isDeleted && tutor?.active !== false && approvalStatus !== "REJECTED";
          }).length;

          console.log("Tutor count (frontend filtered):", tutorCount);
        } catch (error) {
          console.error("Error fetching tutor count:", error);
        }

        // Fetch parents and exclude deactivated/deleted entries
        let parentCount = 0;
        try {
          const parents = [];
          let currentPage = 1;
          let totalPages = 1;

          do {
            const parentResponse = await api.get("/parent", {
              params: { page: currentPage, limit: 100 },
            });

            console.log("Parent response:", parentResponse.data);

            const pageParents = extractListData(parentResponse.data, "parents");
            parents.push(...pageParents);

            totalPages = getTotalPages(parentResponse.data);
            currentPage += 1;
          } while (currentPage <= totalPages);

          parentCount = parents.filter(
            (parent) => !parent?.isDeleted && parent?.active !== false
          ).length;

          console.log("Parent count (frontend filtered):", parentCount);
        } catch (error) {
          console.error("Error fetching parent count:", error);
        }

        // Fetch students count
        let studentCount = 0;
        try {
          const studentsResponse = await api.get("/student");
          console.log("Students response for count:", studentsResponse.data);
          studentCount = studentsResponse.data.count || 0;
          console.log("Student count:", studentCount);
        } catch (error) {
          console.error("Error fetching student count:", error);
        }

        // Fetch subjects count
        let subjectCount = 0;
        try {
          const subjectsResponse = await api.get("/subjects");
          console.log("Subjects response:", subjectsResponse.data);
          subjectCount = subjectsResponse.data.count || 0;
          console.log("Subject count:", subjectCount);
        } catch (error) {
          console.error("Error fetching subject count:", error);
        }

        // Fetch categories count
        let categoryCount = 0;
        try {
          const categoriesResponse = await api.get("/categories");
          console.log("Categories response:", categoriesResponse.data);
          const categories = categoriesResponse.data.data || [];
          const activeCategories = categories.filter((categoryItem) => !categoryItem.isDeleted);
          categoryCount = activeCategories.length;
          console.log("Category count:", categoryCount);
        } catch (error) {
          console.error("Error fetching category count:", error);
        }

        // Fetch support tickets
        try {
          const supportResponse = await api.get("/support-tickets", {
            params: { limit: 1000 },
          });
          console.log("Support tickets response:", supportResponse.data);
          const tickets = supportResponse.data.data || [];
          const total = tickets.length;
          const open = tickets.filter((t) => t.status === "OPEN").length;
          const closed = tickets.filter((t) => t.status === "CLOSED").length;
          const terminated = tickets.filter((t) => t.status === "TERMINATED").length;
          const onHold = tickets.filter((t) => t.status === "ON_HOLD").length;

          setSupportStats({ total, open, closed, terminated, onHold });
          console.log("Support stats:", { total, open, closed, terminated, onHold });
        } catch (error) {
          console.error("Error fetching support stats:", error);
        }

        setCounts({
          tutors: tutorCount,
          parents: parentCount,
          students: studentCount,
          subjects: subjectCount,
          categories: categoryCount,
        });
      } catch (error) {
        console.error("Error in fetchCounts:", error);
      }
    };
    fetchCounts();
  }, []);

  useEffect(() => {
    const fetchFinancialStats = async () => {
      try {
        const normalizeBucket = (bucket = {}) => {
          let expense = bucket.expense || 0;
          const revenue = bucket.revenue || 0;
          const margin = bucket.grossMargin || 0;

          if (expense === 0 && revenue > 0 && margin > 0) {
            expense = revenue - margin;
          }

          return {
            expense,
            revenue,
            grossMargin: margin,
          };
        };

        // Full totals power the cards; current month powers the cycle summary below.
        const [fullComparisonResponse, currentMonthResponse] = await Promise.all([
          getFinancialComparison(),
          getFinancialComparison({ cycle: "currentMonth" }),
        ]);

        const fullPayload = fullComparisonResponse?.data || {};
        const expectedBucket = normalizeBucket(fullPayload.expected || {});
        const currentBucket = normalizeBucket(fullPayload.current || {});

        const currentMonthPayload = currentMonthResponse?.data || {};
        const currentMonthBucket = normalizeBucket(currentMonthPayload.current || {});

        console.log("💡 Final Financial Data:", {
          expected: expectedBucket,
          current: currentBucket,
          currentMonth: currentMonthBucket,
        });

        setFinancialComparison({
          expected: expectedBucket,
          current: currentBucket,
        });

        setCurrentCycleFinancials(currentMonthBucket);

        console.log("💾 Financial Stats Saved to State:", {
          expected: expectedBucket,
          current: currentBucket,
          currentMonth: currentMonthBucket,
        });

      } catch (error) {
        console.error("❌ Error fetching financial comparison:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchFinancialStats();
  }, []);

  useEffect(() => {
    const fetchTutorSalaryOwed = async () => {
      try {
        const response = await getRevenueSnapshot();
        setTutorSalaryOwed(Number(response?.data?.tutorSalaryOwed?.amount || 0));
      } catch (error) {
        console.error("Error fetching tutor salary owed:", error);
      }
    };

    fetchTutorSalaryOwed();
  }, []);

  useEffect(() => {
    const fetchClientAnalytics = async () => {
      try {
        const [allParents, classSessions] = await Promise.all([
          fetchAllParents(),
          fetchAllClassSessions(),
        ]);

        const activeSessionStatuses = new Set(["ONGOING", "SCHEDULED", "CREATED"]);
        const activeParentIds = new Set(
          classSessions
            .filter(
              (session) =>
                !session?.isDeleted && activeSessionStatuses.has(session?.status)
            )
            .map((session) => getParentIdValue(session?.parentId))
            .filter(Boolean)
            .map(String)
        );

        const availableParents = allParents.filter((parent) => !parent?.isDeleted);
        const activeClients = availableParents.filter((parent) =>
          activeParentIds.has(String(parent?._id || parent?.id))
        );
        const inactiveParents = availableParents.filter(
          (parent) => !activeParentIds.has(String(parent?._id || parent?.id))
        );

        // Calculate date ranges
        const now = new Date();
        const twentyEightDaysAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
        const fiftySixDaysAgo = new Date(now.getTime() - 56 * 24 * 60 * 60 * 1000);

        const clientsLast28Days = activeClients.filter(
          (client) => client.createdAt && new Date(client.createdAt) >= twentyEightDaysAgo
        );
        const clientsPrevious28Days = activeClients.filter(
          (client) =>
            client.createdAt &&
            new Date(client.createdAt) >= fiftySixDaysAgo &&
            new Date(client.createdAt) < twentyEightDaysAgo
        );

        const recentClients = [...activeClients]
          .filter((client) => client.createdAt)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 4);

        const enquiriesLast28Days = inactiveParents.filter(
          (parent) => parent.createdAt && new Date(parent.createdAt) >= twentyEightDaysAgo
        ).length;
        const enquiriesPrevious28Days = inactiveParents.filter(
          (parent) =>
            parent.createdAt &&
            new Date(parent.createdAt) >= fiftySixDaysAgo &&
            new Date(parent.createdAt) < twentyEightDaysAgo
        ).length;

        console.log("Client analytics classification:", {
          activeClients: activeClients.length,
          inactiveParentEnquiries: inactiveParents.length,
          recentClients: recentClients.length,
          clientsLast28Days: clientsLast28Days.length,
          enquiriesLast28Days,
        });

        setClientAnalytics({
          recentClients,
          clientsCreated28Days: clientsLast28Days.length,
          clientsCreatedPrevious28Days: clientsPrevious28Days.length,
          enquiries28Days: enquiriesLast28Days,
          enquiriesPrevious28Days: enquiriesPrevious28Days,
        });
      } catch (error) {
        console.error("Error fetching client analytics:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchClientAnalytics();
  }, []);

  useEffect(() => {
    const fetchTutorAnalytics = async () => {
      try {
        const allTutors = await fetchAllTutors({ sort: "createdAt", order: "desc" });

        console.log("Extracted tutors count:", allTutors.length);
        console.log("Sample tutor data:", allTutors[0]);

        // Calculate date ranges
        const now = new Date();
        const twentyEightDaysAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
        const fiftyFiveDaysAgo = new Date(now.getTime() - 56 * 24 * 60 * 60 * 1000);

        // Filter tutors by date ranges
        const tutorsLast28Days = allTutors.filter((tutor) => {
          const createdDate = getTutorCreatedDate(tutor);
          return createdDate && createdDate >= twentyEightDaysAgo;
        });
        const tutorsPrevious28Days = allTutors.filter((tutor) => {
          const createdDate = getTutorCreatedDate(tutor);
          return createdDate && createdDate >= fiftyFiveDaysAgo && createdDate < twentyEightDaysAgo;
        });

        console.log("Tutors created in last 28 days:", tutorsLast28Days.length);
        console.log("Tutors created in previous 28 days:", tutorsPrevious28Days.length);

        const recentTutors = [...tutorsLast28Days]
          .sort((a, b) => getTutorCreatedDate(b) - getTutorCreatedDate(a))
          .slice(0, 4);

        console.log(
          "Recent tutors to display:",
          recentTutors.map((t) => ({ name: t.fullName, created: t.createdAt }))
        );

        const tutorMeetings = allTutors.map((tutor) => tutor?.meeting).filter(Boolean);

        const interviewInvitesLast28Days = tutorMeetings.filter((meeting) => {
          const meetingDate = getMeetingInviteDate(meeting);
          return meetingDate && meetingDate >= twentyEightDaysAgo;
        }).length;

        const interviewInvitesPrevious28Days = tutorMeetings.filter((meeting) => {
          const meetingDate = getMeetingInviteDate(meeting);
          return meetingDate && meetingDate >= fiftyFiveDaysAgo && meetingDate < twentyEightDaysAgo;
        }).length;

        setTutorAnalytics({
          recentTutors,
          tutorsCreated28Days: tutorsLast28Days.length,
          tutorsCreatedPrevious28Days: tutorsPrevious28Days.length,
          interviewInvites28Days: interviewInvitesLast28Days,
          interviewInvitesPrevious28Days: interviewInvitesPrevious28Days,
        });
      } catch (error) {
        console.error("Error fetching tutor analytics:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchTutorAnalytics();
  }, []);

  useEffect(() => {
    const fetchPaymentStats = async () => {
      try {
        // 1. Fetch pending payments from Finance (PENDING status invoices)
        const financeResponse = await api.get("/finance/get-all-raise-invoice", {
          params: {
            status: "PENDING",
            limit: 100,
            page: 1,
          },
        });
        const pendingInvoices = financeResponse.data.data || [];
        const pendingCount = pendingInvoices.length;
        const pendingAmount = pendingInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

        // 2. Fetch expired packages from class sessions
        // A package is treated as expired when class cycle/classes are completed.
        const classSessionsResponse = await api.get("/classes/sessions/all", {
          params: {
            limit: 10000,
            page: 1,
          },
        });

        console.log("Class sessions response for expired packages:", classSessionsResponse.data);

        const classSessionsData = classSessionsResponse?.data || {};
        const allClassSessions =
          classSessionsData?.data?.classSessions ||
          classSessionsData?.classSessions ||
          classSessionsData?.data ||
          [];

        const expiredClassSessions = (
          Array.isArray(allClassSessions) ? allClassSessions : []
        ).filter((session) => {
          const status = String(session?.status || "").toUpperCase();
          const totalClasses = toNumber(session?.totalNoOfClasses);
          const completedClasses = toNumber(session?.completedClasses);
          const paymentAmount = toNumber(session?.paymentAmount);

          const hasPackage = totalClasses > 0 || paymentAmount > 0;
          const isCycleCompleted = totalClasses > 0 && completedClasses >= totalClasses;
          const isCompletedStatus = status === "COMPLETED";

          return !session?.isDeleted && hasPackage && (isCycleCompleted || isCompletedStatus);
        });

        const expiredCount = expiredClassSessions.length;
        const expiredAmount = expiredClassSessions.reduce(
          (sum, session) => sum + toNumber(session?.paymentAmount),
          0
        );
        const now = new Date();

        // 3. Calculate expected recurring payment - all active class sessions this month
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        // Get all active class sessions to calculate expected payments
        const classesResponse = await api.get("/classes", {
          params: {
            status: "ACTIVE",
            limit: 100,
          },
        });

        const activeClasses = classesResponse.data.data || [];
        let expectedRecurring = 0;

        // Calculate expected payment based on active sessions
        activeClasses.forEach((cls) => {
          if (cls.cycleType === "MONTHLY") {
            // For monthly cycles, add the monthly tuition fee
            expectedRecurring += cls.tuitionFee || 0;
          } else if (cls.cycleType === "WEEKLY") {
            // For weekly cycles, multiply by ~4 weeks in a month
            expectedRecurring += (cls.tuitionFee || 0) * 4;
          }
        });

        // If no active classes, fallback to pending invoices
        if (expectedRecurring === 0) {
          const allPendingResponse = await api.get("/finance/get-all-raise-invoice", {
            params: {
              status: "PENDING",
              limit: 100,
            },
          });
          const allPending = allPendingResponse.data.data || [];
          expectedRecurring = allPending.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
        }

        // 4. Calculate expected tutor payout for last 30 days

        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
        thirtyDaysAgo.setHours(0, 0, 0, 0);

        const todayEnd = new Date(now);
        todayEnd.setHours(23, 59, 59, 999);

        const doesCycleOverlapWindow = (cycle, windowStart, windowEnd) => {
          const cycleStart = cycle?.cycleStartDate ? new Date(cycle.cycleStartDate) : null;
          const cycleEnd = cycle?.cycleEndDate ? new Date(cycle.cycleEndDate) : null;

          const hasValidStart = cycleStart && !Number.isNaN(cycleStart.getTime());
          const hasValidEnd = cycleEnd && !Number.isNaN(cycleEnd.getTime());

          if (hasValidStart && hasValidEnd) {
            return cycleStart <= windowEnd && cycleEnd >= windowStart;
          }

          if (hasValidEnd) {
            return cycleEnd >= windowStart && cycleEnd <= windowEnd;
          }

          if (hasValidStart) {
            return cycleStart >= windowStart && cycleStart <= windowEnd;
          }

          return false;
        };

        // Fetch all payment cycles across pages so dashboard totals are complete.
        const allPaymentCycles = [];
        let currentPage = 1;
        let totalPages = 1;

        do {
          const cyclesResponse = await getAllTutorPaymentCycles({
            page: currentPage,
            limit: 100,
          });

          const pageData = Array.isArray(cyclesResponse?.data) ? cyclesResponse.data : [];
          allPaymentCycles.push(...pageData);

          totalPages = Number(cyclesResponse?.pagination?.totalPages) || 1;
          currentPage += 1;
        } while (currentPage <= totalPages);

        // Include only non-terminal cycles that overlap the last 30 days window.
        const currentWindowPaymentCycles = allPaymentCycles.filter((cycle) => {
          const status = String(cycle?.status || "").toUpperCase();
          const isPayableStatus = status !== "REJECTED" && status !== "FAILED";

          return isPayableStatus && doesCycleOverlapWindow(cycle, thirtyDaysAgo, todayEnd);
        });

        // Expected per cycle = net cycle amount, fallback to class-based estimate.
        const expectedSalary = currentWindowPaymentCycles.reduce((sum, cycle) => {
          const totalClasses = toNumber(cycle?.totalClasses);
          const completedClasses = toNumber(cycle?.completedClasses);
          const sessionTotalClasses = toNumber(cycle?.financeId?.classSessionId?.totalNoOfClasses);

          // Prefer planned cycle classes; fallback to completed/session classes if missing
          const payableClasses = totalClasses || completedClasses || sessionTotalClasses;

          // Finance tutorPayRate may be zero in old data; fallback to classSession tutorSalary
          const tutorPayRate =
            toNumber(cycle?.financeId?.tutorPayRate) ||
            toNumber(cycle?.financeId?.classSessionId?.tutorSalary);

          const formulaAmount =
            payableClasses > 0 && tutorPayRate > 0 ? payableClasses * tutorPayRate : 0;

          const calculatedAmount = toNumber(cycle?.calculatedAmount);
          const bonusAmount = toNumber(cycle?.bonusAmount);
          const deductionAmount = toNumber(cycle?.deductionAmount);
          const netCycleAmount = calculatedAmount + bonusAmount - deductionAmount;

          const fallbackAmount = calculatedAmount;

          return sum + (netCycleAmount > 0 ? netCycleAmount : formulaAmount || fallbackAmount);
        }, 0);

        setPaymentStats({
          pendingPayments: { count: pendingCount, amount: pendingAmount },
          expiredPackages: { count: expiredCount, amount: expiredAmount },
          expectedRecurringPayment: expectedRecurring,
          expectedTutorSalary: expectedSalary,
        });

        console.log("Payment Stats:", {
          pendingPayments: { count: pendingCount, amount: pendingAmount },
          expiredPackages: { count: expiredCount, amount: expiredAmount },
          expectedRecurring,
          expectedSalary,
          paymentCyclesCount: allPaymentCycles.length,
          currentWindowPaymentCyclesCount: currentWindowPaymentCycles.length,
        });
      } catch (error) {
        console.error("Error fetching payment stats:", error);
      }
    };

    fetchPaymentStats();
  }, []);

  const buildExpectedFinancialRows = (invoices = []) =>
    (Array.isArray(invoices) ? invoices : []).map((invoice) => {
      const parentName = invoice?.parentId?.fullName || "N/A";
      const studentName =
        invoice?.students?.[0]?.fullName ||
        invoice?.students?.[0]?.name ||
        invoice?.studentId?.fullName ||
        "N/A";

      const className = invoice?.students?.[0]?.class?.name || "N/A";
      const subjectName = invoice?.students?.[0]?.subject?.name || "N/A";
      const date = invoice?.createdAt ? dayjs(invoice.createdAt).format("DD MMM YYYY") : "N/A";
      const status = invoice?.status || "UNKNOWN";

      const tuitionFees =
        toNumber(invoice?.totalAmount) || toNumber(invoice?.classSessionId?.[0]?.tuitionFee);

      const tutorSalary = toNumber(invoice?.classSessionId?.[0]?.tutorSalary);
      const totalClasses =
        toNumber(invoice?.totalClasses) || toNumber(invoice?.classSessionId?.[0]?.totalNoOfClasses);
      const completedClasses =
        toNumber(invoice?.completedClasses) ||
        toNumber(invoice?.creditConsumedClasses) ||
        toNumber(invoice?.classSessionId?.[0]?.completedClasses);
      const hasRemainingClasses =
        invoice?.remainingClasses !== undefined && invoice?.remainingClasses !== null;
      const remainingClasses = hasRemainingClasses
        ? toNumber(invoice.remainingClasses)
        : totalClasses > 0
        ? Math.max(totalClasses - completedClasses, 0)
        : null;
      const tutorPay = toNumber(invoice?.totalTutorPay) || tutorSalary * totalClasses;

      return {
        id: invoice?._id || `${parentName}-${studentName}-${Math.random()}`,
        date,
        parentName,
        studentName,
        className,
        subjectName,
        status,
        tuitionFees,
        tutorPay,
        remainingClasses,
      };
    });

  const loadExpectedFinancials = async () => {
    setExpectedFinancialLoading(true);
    try {
      const response = await api.get("/finance/get-all-raise-invoice", {
        params: {
          status: "PENDING",
          expectedFinancialOnly: true,
          page: 1,
          limit: 100,
        },
      });

      const invoices = response?.data?.data || [];
      invoices.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setExpectedFinancialRows(buildExpectedFinancialRows(invoices));
    } catch (error) {
      console.error("Error fetching expected financial details:", error);
      setExpectedFinancialRows([]);
    } finally {
      setExpectedFinancialLoading(false);
    }
  };

  const handleOpenExpectedFinancials = () => {
    setExpectedFinancialOpen(true);
    loadExpectedFinancials();
  };

  const handleCloseExpectedFinancials = () => {
    setExpectedFinancialOpen(false);
  };

  const fetchAllPaidInvoices = async () => {
    const invoices = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await api.get("/finance/get-all-raise-invoice", {
        params: { status: "PAID", page, limit: 100 },
      });

      const pageInvoices = response?.data?.data || [];
      const pagination = response?.data?.pagination || {};

      invoices.push(...(Array.isArray(pageInvoices) ? pageInvoices : []));
      totalPages = Number(pagination.totalPages) || 1;
      page += 1;
    } while (page <= totalPages);

    return invoices;
  };

  const fetchAllClassSessions = async () => {
    const sessions = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await api.get("/classes/sessions/all", {
        params: { page, limit: 500 },
      });

      const payload = response?.data || {};
      const pageSessions =
        payload?.data?.classSessions || payload?.classSessions || payload?.data || [];
      const pagination = payload?.pagination || {};

      sessions.push(...(Array.isArray(pageSessions) ? pageSessions : []));
      totalPages = Number(pagination.totalPages) || 1;
      page += 1;
    } while (page <= totalPages);

    return sessions;
  };

  useEffect(() => {
    const fetchLeadStats = async () => {
      try {
        const [allLeads, paidInvoices, classSessions] = await Promise.all([
          fetchAllCrmEntries({ type: "PARENT" }),
          fetchAllPaidInvoices(),
          fetchAllClassSessions(),
        ]);

        console.log("All Leads:", allLeads);
        console.log("Total Leads Count:", allLeads.length);

        if (!Array.isArray(allLeads)) {
          console.error("allLeads is not an array:", allLeads);
          return;
        }

        // Log all unique followUpStatus values to see what we're working with
        const uniqueStatuses = [...new Set(allLeads.map((lead) => lead.followUpStatus))];
        const uniqueParentStatuses = [...new Set(allLeads.map((lead) => lead.parentStatus))];
        console.log("Unique Follow-Up Statuses:", uniqueStatuses);
        console.log("Unique Parent Statuses:", uniqueParentStatuses);

        const {
          leadStatusUnits,
          prospectUnits,
          tutorFoundUnits,
          demoFixedUnits,
          convertedUnits,
          prospect,
          tutorFound,
          demoFixed,
          converted,
        } = getLeadStageStats(allLeads);

        const leadParentIds = new Set(
          allLeads.map((lead) => getParentIdValue(lead?.parentId)).filter(Boolean)
        );

        const totalConvertedLeadIds = new Set();

        allLeads.forEach((lead) => {
          const parentId = getParentIdValue(lead?.parentId);
          if (!parentId) return;

          const isConvertedStatus =
            lead.followUpStatus === "Won" ||
            lead.followUpStatus === "PAID" ||
            lead.followUpStatus === "OTM - PAID" ||
            lead.followUpStatus === "OTM - completed" ||
            lead.parentStatus === "COMPLETED" ||
            lead.parentStatus === "ONE_TIME_PLAN";

          if (isConvertedStatus) {
            totalConvertedLeadIds.add(parentId);
          }
        });

        const paidParentIds = new Set();

        paidInvoices.forEach((invoice) => {
          const parentId = getParentIdValue(invoice?.parentId);
          if (parentId) {
            paidParentIds.add(parentId);
          }
        });

        classSessions.forEach((session) => {
          const paymentStatus = String(session?.paymentStatus || "").toUpperCase();
          const isPaidSession =
            paymentStatus === "COMPLETED" || session?.isPaymentVerified === true;
          if (!isPaidSession) return;

          const parentId = getParentIdValue(session?.parentId);
          if (parentId) {
            paidParentIds.add(parentId);
          }
        });

        paidParentIds.forEach((parentId) => {
          if (leadParentIds.has(parentId)) {
            totalConvertedLeadIds.add(parentId);
          }
        });

        // Keep the existing broad payment/class-based Total Converted calculation.
        const totalConverted = totalConvertedLeadIds.size;

        // Log detailed breakdown
        console.log("Lead Stage Breakdown:");
        console.log(
          "- Effective Status Units:",
          leadStatusUnits.map((unit) => ({
            followUp: unit.status,
            parent: unit.parentStatus,
            studentId: unit.studentId,
          }))
        );
        console.log(
          "- Prospect:",
          prospect,
          prospectUnits.map((unit) => ({
            followUp: unit.status,
            parent: unit.parentStatus,
            studentId: unit.studentId,
          }))
        );
        console.log(
          "- Tutor Found:",
          tutorFound,
          tutorFoundUnits.map((unit) => ({
            followUp: unit.status,
            parent: unit.parentStatus,
            studentId: unit.studentId,
          }))
        );
        console.log(
          "- Demo Fixed:",
          demoFixed,
          demoFixedUnits.map((unit) => unit.status)
        );
        console.log(
          "- Converted:",
          converted,
          convertedUnits.map((unit) => ({ followUp: unit.status, parent: unit.parentStatus }))
        );

        // Calculate conversion rate: (converted / total leads) * 100
        const totalLeads = prospect;
        const conversionRate = totalLeads > 0 ? ((converted / totalLeads) * 100).toFixed(1) : 0;

        setLeadStats({
          prospect,
          tutorFound,
          demoFixed,
          converted,
          totalConverted,
          conversionRate,
        });

        console.log("Lead Stats Updated:", {
          prospect,
          tutorFound,
          demoFixed,
          converted,
          totalConverted,
          conversionRate: conversionRate + "%",
          totalLeads,
        });
      } catch (error) {
        console.error("Error fetching lead stats:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchLeadStats();
  }, []);

  // Calculate percentage changes
  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const clientPercentageChange = calculatePercentageChange(
    clientAnalytics.clientsCreated28Days,
    clientAnalytics.clientsCreatedPrevious28Days
  );

  const enquiryPercentageChange = calculatePercentageChange(
    clientAnalytics.enquiries28Days,
    clientAnalytics.enquiriesPrevious28Days
  );

  const tutorPercentageChange = calculatePercentageChange(
    tutorAnalytics.tutorsCreated28Days,
    tutorAnalytics.tutorsCreatedPrevious28Days
  );

  const interviewPercentageChange = calculatePercentageChange(
    tutorAnalytics.interviewInvites28Days,
    tutorAnalytics.interviewInvitesPrevious28Days
  );

  const currentRevenueDisplay = Number(financialComparison.current.revenue || 0);
  const currentExpenseDisplay = Number(financialComparison.current.expense || 0);
  const currentMarginDisplay = Number(financialComparison.current.grossMargin || 0);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox
        // display="flex"
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
        // justifyContent="space-evenly"

        flexWrap="wrap"
        mt={3}
      >
        <Link to="/tutors" style={{ textDecoration: "none" }}>
          <DetailedStatisticsCard
            bgColor="white"
            title="Total Tutors"
            width="100%"
            count={counts.tutors}
            icon={{ color: "info", component: <i className="material-icons">school</i> }}
            percentage={{
              color: "success",
              count: "+2%",
              text: "since last week",
            }}
          />
        </Link>
        <Link to="/parents" style={{ textDecoration: "none" }}>
          <DetailedStatisticsCard
            bgColor="white"
            title="Total Parents"
            width="100%"
            count={counts.parents}
            icon={{
              color: "success",
              component: <i className="material-icons">group</i>,
            }}
            percentage={{
              color: "success",
              count: "+5%",
              text: "since last month",
            }}
          />
        </Link>
        <DetailedStatisticsCard
          bgColor="white"
          title="Total Students"
          count={counts.students}
          icon={{
            color: "warning",
            component: <i className="material-icons">emoji_people</i>,
          }}
          percentage={{ color: "error", count: "-1%", text: "this month" }}
        />
        <Link to="/curriculum" style={{ textDecoration: "none" }}>
          <DetailedStatisticsCard
            bgColor="white"
            title="Total category"
            count={counts.categories}
            icon={{
              color: "primary",
              component: <i className="material-icons">category</i>,
            }}
            percentage={{
              color: "success",
              count: "+3%",
              text: "from last week",
            }}
          />
        </Link>
        <Link to="/curriculum?tab=subject" style={{ textDecoration: "none" }}>
          <DetailedStatisticsCard
            bgColor="white"
            title="Total subjects"
            count={counts.subjects}
            icon={{
              color: "secondary",
              component: <i className="material-icons">menu_book</i>,
            }}
            percentage={{
              color: "success",
              count: "+8%",
              text: "compared to last month",
            }}
          />
        </Link>
      </ArgonBox>

      <ArgonBox py={3}>
        <Grid container spacing={3}>
          {/* Client Analytics - Horizontal Card */}
          <Grid item xs={12} lg={6}>
            <ClientAnalyticsCard
              clientsCreated={clientAnalytics.clientsCreated28Days}
              enquiries={clientAnalytics.enquiries28Days}
              recentClients={clientAnalytics.recentClients}
              percentageChange={{
                clients: clientPercentageChange,
                enquiries: enquiryPercentageChange,
              }}
            />
          </Grid>

          {/* Tutor Analytics - Horizontal Card */}
          <Grid item xs={12} lg={6}>
            <TutorAnalyticsCard
              tutorsCreated={tutorAnalytics.tutorsCreated28Days}
              interviewInvites={tutorAnalytics.interviewInvites28Days}
              recentTutors={tutorAnalytics.recentTutors}
              percentageChange={{
                tutors: tutorPercentageChange,
                interviews: interviewPercentageChange,
              }}
            />
          </Grid>

          {/* Expected Financials */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Expected Financials"
              action={
                <Tooltip title="View upcoming students">
                  <IconButton size="small" onClick={handleOpenExpectedFinancials}>
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              stats={[
                {
                  label: "Revenue",
                  value: formatCurrency(financialComparison.expected.revenue),
                },
                {
                  label: "Expense",
                  value: formatCurrency(financialComparison.expected.expense),
                },
                {
                  label: "Gross Margin",
                  value: formatCurrency(financialComparison.expected.grossMargin),
                },
              ]}
            />
          </Grid>

          {/* Current Financials */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Current Financials (Total)"
              stats={[
                {
                  label: "Revenue",
                  value: formatCurrency(currentRevenueDisplay),
                },
                {
                  label: "Expense",
                  value: formatCurrency(currentExpenseDisplay),
                },
                {
                  label: "Gross Margin",
                  value: formatCurrency(currentMarginDisplay),
                },
              ]}
            />
          </Grid>

          {/* Tutor Section */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Tutor Section"
              stats={[
                { label: "Teachers Onboarded", value: counts.tutors.toString() },
                { label: "Revenue", value: formatCurrency(currentRevenueDisplay) },
                {
                  label: "Tutor Salary (Owed)",
                  value: formatCurrency(tutorSalaryOwed),
                },
              ]}
            />
          </Grid>

          {/* Customer Support - Horizontal */}
          <Grid item xs={12} md={12} lg={12}>
            <StatCard
              title="Customer Support"
              horizontal
              stats={[
                { label: "Total", value: supportStats.total.toString() },
                { label: "Open", value: supportStats.open.toString() },
                { label: "Closed", value: supportStats.closed.toString() },
                { label: "Terminated", value: supportStats.terminated.toString() },
                { label: "On Hold", value: supportStats.onHold.toString() },
              ]}
            />
          </Grid>

          {/* Payments & Finance Section */}
          <Grid item xs={12} md={12} lg={12}>
            <ArgonTypography variant="h5" fontWeight="bold" mb={2} mt={2}>
              💳 Payments & Finance
            </ArgonTypography>
          </Grid>

          {/* Pending Payments */}
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Pending Payments"
              stats={[
                { label: "Count", value: paymentStats.pendingPayments.count.toString() },
                { label: "Amount", value: formatCurrency(paymentStats.pendingPayments.amount) },
              ]}
            />
          </Grid>

          {/* Expired Packages */}
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Expired Packages"
              stats={[
                { label: "Count", value: paymentStats.expiredPackages.count.toString() },
                { label: "Amount", value: formatCurrency(paymentStats.expiredPackages.amount) },
              ]}
            />
          </Grid>

          {/* Expected Recurring Payment */}
          {/* <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Expected Recurring (This Month)"
              stats={[
                { label: "Amount", value: formatCurrency(paymentStats.expectedRecurringPayment) },
              ]}
            />
          </Grid> */}

          {/* Expected Tutor Salary */}
          {/* <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Expected Tutor Salary (This Month)"
              stats={[{ label: "Amount", value: formatCurrency(currentCycleFinancials.expense) }]}
            />
          </Grid> */}

          {/* Lead & CRM Management Section */}
          <Grid item xs={12} md={12} lg={12}>
            <ArgonTypography variant="h5" fontWeight="bold" mb={2} mt={2}>
              📊 Lead & CRM Management
            </ArgonTypography>
          </Grid>

          {/* Lead Funnel Stages */}
          <Grid item xs={12} md={12} lg={8}>
            <StatCard
              title="Lead Funnel Stages"
              horizontal
              stats={[
                { label: "Prospect", value: leadStats.prospect.toString() },
                { label: "Tutor Found", value: leadStats.tutorFound.toString() },
                { label: "Demo Fixed", value: leadStats.demoFixed.toString() },
                { label: "Converted", value: leadStats.converted.toString() },
              ]}
            />
          </Grid>

          {/* Conversion Analytics */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Conversion Analytics"
              stats={[
                { label: "Lead to Client", value: leadStats.conversionRate + "%" },
                { label: "Total Converted", value: leadStats.totalConverted.toString() },
              ]}
            />
          </Grid>
        </Grid>
      </ArgonBox>

      <ArgonBox py={3}>
        {/* <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total Tutors"
              count="328"
              icon={{ color: "info", component: <FitnessCenterIcon /> }}
              percentage={{ color: "success", count: "+55%", text: "since yesterday" }} />
              
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total parents"
              count="66"
              icon={{ color: "error", component: <i className="ni ni-world" /> }}
              percentage={{ color: "success", count: "+3%", text: "since last week" }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total Students"
              count="115"
              icon={{ color: "success", component: <i className="ni ni-paper-diploma" /> }}
              percentage={{ color: "warning", count: "-2%", text: "since last quarter" }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total Categories"
              count={counts.categories}
              icon={{ color: "warning", component: <i className="ni ni-cart" /> }}
              percentage={{ color: "success", count: "+5%", text: "than last month" }}
            />
          </Grid>
          
            
        </Grid> */}

        {/* <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={12}>
            <GradientLineChart
              title="Sales Overview"
              description={
                <ArgonBox display="flex" alignItems="center">
                  <ArgonBox fontSize={size.lg} color="success" mb={0.3} mr={0.5} lineHeight={0}>
                    <Icon sx={{ fontWeight: "bold" }}>arrow_upward</Icon>
                  </ArgonBox>
                  <ArgonTypography variant="button" color="text" fontWeight="medium">
                    4% more{" "}
                    <ArgonTypography variant="button" color="text" fontWeight="regular">
                      in 2022
                    </ArgonTypography>
                  </ArgonTypography>
                </ArgonBox>
              }
              chart={gradientLineChartData}
            />
          </Grid>
          <Grid item xs={12} lg={5}>
            <Slider />
          </Grid>
        </Grid> */}
        <Grid container spacing={3}>
          {/* <Grid item xs={12} md={8}>
            <SalesTable title="Sales by Country" rows={salesTableData} />
          </Grid> */}
          {/* <Grid item xs={12} md={4}>
            <CategoriesList title="categories" categories={categoriesListData} />
          </Grid> */}
        </Grid>
      </ArgonBox>
      <Footer />

      <Dialog
        open={expectedFinancialOpen}
        onClose={handleCloseExpectedFinancials}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
            <ArgonTypography variant="h6" fontWeight="bold" color="dark">
              Financials - Students Records
            </ArgonTypography>
            <IconButton size="small" onClick={handleCloseExpectedFinancials}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {expectedFinancialLoading ? (
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="h6" color="text">
                Loading financials...
              </ArgonTypography>
            </ArgonBox>
          ) : expectedFinancialRows.length === 0 ? (
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="h6" color="text">
                No financial records found.
              </ArgonTypography>
            </ArgonBox>
          ) : (
            <ArgonBox sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell align="left" sx={{ pl: 3, py: 2 }}>
                      Date
                    </TableCell>
                    <TableCell align="left" sx={{ py: 2 }}>
                      Parent Name
                    </TableCell>
                    <TableCell align="left" sx={{ py: 2 }}>
                      Student / Class
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      Tuition Fees
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      Tutor Pay
                    </TableCell>
                    <TableCell align="center" sx={{ pr: 3, py: 2 }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expectedFinancialRows
                    .filter((row) => row.status === "PENDING")
                    .map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ pl: 3 }}>
                          <ArgonTypography variant="caption" color="text" fontWeight="medium">
                            {row.date}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="button" fontWeight="medium" color="dark">
                            {row.parentName}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonBox display="flex" flexDirection="column">
                            <ArgonTypography variant="button" fontWeight="medium" color="dark">
                              {row.studentName}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="secondary">
                              {row.remainingClasses !== null
                                ? `${row.className} - ${row.remainingClasses} remaining classes`
                                : row.className}
                            </ArgonTypography>
                          </ArgonBox>
                        </TableCell>
                        <TableCell align="center">
                          <ArgonTypography variant="caption" color="info" fontWeight="bold">
                            {formatCurrency(row.tuitionFees)}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell align="center">
                          <ArgonTypography variant="caption" color="error" fontWeight="bold">
                            {formatCurrency(row.tutorPay)}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell align="center" sx={{ pr: 3 }}>
                          <ArgonBadge
                            variant="gradient"
                            badgeContent={row.status}
                            color={
                              row.status === "PAID"
                                ? "success"
                                : row.status === "PENDING"
                                ? "warning"
                                : "error"
                            }
                            size="xs"
                            container
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </ArgonBox>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default Default;
